import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import isEqual from "lodash/isEqual";
import { useCallback, useMemo } from "react";

import {
  ConversationModel,
  getConversationToken as getConversationModelToken,
  isConversationOtherPartyBrandUser as isConversationModelOtherPartyBrandUser,
  isConversationUnRead as isConversationModelUnRead,
  isConversationStarredByBrand as isConversationModelStarredByBrand,
  getConversationMessageCount as getConversationModelMessageCount,
  getConversationOtherPartyToken as getConversationModelOtherPartyUserToken,
} from "./ConversationModel";
import { MainStreamFetchState } from "./MainStreamFetchState";
import { useMessengerContext } from "./MessengerContext";

/**
 * Store updated conversations in the main stream fetch state.
 */
export const useMessengerStoreStoreUpdatedConversation = (): {
  storeUpdatedConversation: (updatedConversation: ConversationModel) => void;
  removeRetailerContextConversationIfEmpty: (retailerToken: string) => void;
  removeBrandContextConversationIfEmpty: (brandToken: string) => void;
  updateConversationInFetchState: (
    fetchState: MainStreamFetchState,
    conversation: ConversationModel
  ) => MainStreamFetchState;
  existsInFetchState: (
    fetchState: MainStreamFetchState,
    conversation: ConversationModel
  ) => boolean;
} => {
  const {
    setAllMainStreamFetchState,
    setUnreadOnlyMainStreamFetchState,
    setStarredByBrandOnlyMainStreamFetchState,
  } = useMessengerContext() ?? {};

  const existsInFetchState = useCallback(
    (
      fetchState: MainStreamFetchState,
      conversation: ConversationModel
    ): boolean => {
      const conversationToken = getConversationModelToken(conversation);
      const otherPartyUserToken = conversation.otherParty?.token;

      return (
        (!!conversationToken &&
          fetchState.conversationByConversationToken.has(conversationToken)) ||
        (!!otherPartyUserToken &&
          fetchState.conversationByUserToken.has(otherPartyUserToken))
      );
    },
    []
  );

  const updateConversationInFetchState = useCallback(
    (
      fetchState: MainStreamFetchState,
      conversation: ConversationModel
    ): MainStreamFetchState => {
      const conversationToken = getConversationModelToken(conversation);
      const otherPartyUserToken = conversation.otherParty?.token;
      const brandToken = conversation.brand?.token;
      const retailerToken = conversation.otherParty?.retailer_token;
      const isOtherPartyBrandUser =
        isConversationModelOtherPartyBrandUser(conversation);
      const isOtherPartyRetailerUser =
        conversation.otherParty?.type === IUser.Type.USER;

      let newFetchState = fetchState;
      if (conversationToken) {
        if (
          !isEqual(
            newFetchState.conversationByConversationToken.get(
              conversationToken
            ),
            conversation
          )
        ) {
          const newConversationByConversationToken = new Map(
            newFetchState.conversationByConversationToken
          );
          newConversationByConversationToken.set(
            conversationToken,
            conversation
          );
          newFetchState = {
            ...newFetchState,
            conversationByConversationToken: newConversationByConversationToken,
          };
        }
      }

      if (otherPartyUserToken) {
        if (
          !isEqual(
            newFetchState.conversationByUserToken.get(otherPartyUserToken),
            conversation
          )
        ) {
          const newConversationByUserToken = new Map(
            newFetchState.conversationByUserToken
          );
          newConversationByUserToken.set(otherPartyUserToken, conversation);
          newFetchState = {
            ...newFetchState,
            conversationByUserToken: newConversationByUserToken,
          };
        }
      }

      if (isOtherPartyBrandUser && brandToken) {
        if (
          !isEqual(
            newFetchState.conversationByBrandToken.get(brandToken),
            conversation
          )
        ) {
          const newConversationByBrandToken = new Map(
            newFetchState.conversationByBrandToken
          );
          newConversationByBrandToken.set(brandToken, conversation);
          newFetchState = {
            ...newFetchState,
            conversationByBrandToken: newConversationByBrandToken,
          };
        }
      }

      if (isOtherPartyRetailerUser && retailerToken) {
        if (
          !isEqual(
            newFetchState.conversationByRetailerToken.get(retailerToken),
            conversation
          )
        ) {
          const newConversationByRetailerToken = new Map(
            newFetchState.conversationByRetailerToken
          );
          newConversationByRetailerToken.set(retailerToken, conversation);
          newFetchState = {
            ...newFetchState,
            conversationByRetailerToken: newConversationByRetailerToken,
          };
        }
      }

      return newFetchState;
    },
    []
  );

  const storeUpdatedConversation = useCallback(
    (updatedConversation: ConversationModel): void => {
      if (
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setAllMainStreamFetchState((allMainStreamFetchState) =>
        updateConversationInFetchState(
          allMainStreamFetchState,
          updatedConversation
        )
      );

      setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
        if (
          existsInFetchState(
            unreadOnlyMainStreamFetchState,
            updatedConversation
          ) ||
          isConversationModelUnRead(updatedConversation)
        ) {
          return updateConversationInFetchState(
            unreadOnlyMainStreamFetchState,
            updatedConversation
          );
        } else {
          return unreadOnlyMainStreamFetchState;
        }
      });

      setStarredByBrandOnlyMainStreamFetchState(
        (starredByBrandOnlyMainStreamFetchState) => {
          if (
            existsInFetchState(
              starredByBrandOnlyMainStreamFetchState,
              updatedConversation
            ) ||
            isConversationModelStarredByBrand(updatedConversation)
          ) {
            return updateConversationInFetchState(
              starredByBrandOnlyMainStreamFetchState,
              updatedConversation
            );
          } else {
            return starredByBrandOnlyMainStreamFetchState;
          }
        }
      );
    },
    [
      updateConversationInFetchState,
      existsInFetchState,
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
    ]
  );

  const removeRetailerContextConversationIfEmpty = useCallback(
    (retailerToken: string) => {
      if (
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setAllMainStreamFetchState((allMainStreamFetchState) => {
        const conversation =
          allMainStreamFetchState.conversationByRetailerToken.get(
            retailerToken
          );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByRetailerToken = new Map(
            allMainStreamFetchState.conversationByRetailerToken
          );
          newConversationByRetailerToken.delete(retailerToken);
          const newConversationByUserToken = new Map(
            allMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...allMainStreamFetchState,
            conversationByRetailerToken: newConversationByRetailerToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return allMainStreamFetchState;
        }
      });

      setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
        const conversation =
          unreadOnlyMainStreamFetchState.conversationByRetailerToken.get(
            retailerToken
          );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByRetailerToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByRetailerToken
          );
          newConversationByRetailerToken.delete(retailerToken);
          const newConversationByUserToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...unreadOnlyMainStreamFetchState,
            conversationByRetailerToken: newConversationByRetailerToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return unreadOnlyMainStreamFetchState;
        }
      });

      setStarredByBrandOnlyMainStreamFetchState(
        (starredByBrandOnlyMainStreamFetchState) => {
          const conversation =
            starredByBrandOnlyMainStreamFetchState.conversationByRetailerToken.get(
              retailerToken
            );
          if (
            conversation &&
            getConversationModelMessageCount(conversation) === 0
          ) {
            const newConversationByRetailerToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByRetailerToken
            );
            newConversationByRetailerToken.delete(retailerToken);
            const newConversationByUserToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByUserToken
            );
            newConversationByUserToken.delete(
              getConversationModelOtherPartyUserToken(conversation)
            );
            return {
              ...starredByBrandOnlyMainStreamFetchState,
              conversationByRetailerToken: newConversationByRetailerToken,
              conversationByUserToken: newConversationByUserToken,
            };
          } else {
            return starredByBrandOnlyMainStreamFetchState;
          }
        }
      );
    },
    [
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
    ]
  );

  const removeBrandContextConversationIfEmpty = useCallback(
    (brandToken: string) => {
      if (
        !setAllMainStreamFetchState ||
        !setUnreadOnlyMainStreamFetchState ||
        !setStarredByBrandOnlyMainStreamFetchState
      ) {
        return;
      }

      setAllMainStreamFetchState((allMainStreamFetchState) => {
        const conversation =
          allMainStreamFetchState.conversationByBrandToken.get(brandToken);
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByBrandToken = new Map(
            allMainStreamFetchState.conversationByBrandToken
          );
          newConversationByBrandToken.delete(brandToken);
          const newConversationByUserToken = new Map(
            allMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...allMainStreamFetchState,
            conversationByBrandToken: newConversationByBrandToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return allMainStreamFetchState;
        }
      });

      setUnreadOnlyMainStreamFetchState((unreadOnlyMainStreamFetchState) => {
        const conversation =
          unreadOnlyMainStreamFetchState.conversationByBrandToken.get(
            brandToken
          );
        if (
          conversation &&
          getConversationModelMessageCount(conversation) === 0
        ) {
          const newConversationByBrandToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByBrandToken
          );
          newConversationByBrandToken.delete(brandToken);
          const newConversationByUserToken = new Map(
            unreadOnlyMainStreamFetchState.conversationByUserToken
          );
          newConversationByUserToken.delete(
            getConversationModelOtherPartyUserToken(conversation)
          );
          return {
            ...unreadOnlyMainStreamFetchState,
            conversationByBrandToken: newConversationByBrandToken,
            conversationByUserToken: newConversationByUserToken,
          };
        } else {
          return unreadOnlyMainStreamFetchState;
        }
      });

      setStarredByBrandOnlyMainStreamFetchState(
        (starredByBrandOnlyMainStreamFetchState) => {
          const conversation =
            starredByBrandOnlyMainStreamFetchState.conversationByBrandToken.get(
              brandToken
            );
          if (
            conversation &&
            getConversationModelMessageCount(conversation) === 0
          ) {
            const newConversationByBrandToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByBrandToken
            );
            newConversationByBrandToken.delete(brandToken);
            const newConversationByUserToken = new Map(
              starredByBrandOnlyMainStreamFetchState.conversationByUserToken
            );
            newConversationByUserToken.delete(
              getConversationModelOtherPartyUserToken(conversation)
            );
            return {
              ...starredByBrandOnlyMainStreamFetchState,
              conversationByBrandToken: newConversationByBrandToken,
              conversationByUserToken: newConversationByUserToken,
            };
          } else {
            return starredByBrandOnlyMainStreamFetchState;
          }
        }
      );
    },
    [
      setAllMainStreamFetchState,
      setUnreadOnlyMainStreamFetchState,
      setStarredByBrandOnlyMainStreamFetchState,
    ]
  );

  return useMemo(
    () => ({
      storeUpdatedConversation,
      removeRetailerContextConversationIfEmpty,
      removeBrandContextConversationIfEmpty,
      updateConversationInFetchState,
      existsInFetchState,
    }),
    [
      storeUpdatedConversation,
      removeRetailerContextConversationIfEmpty,
      removeBrandContextConversationIfEmpty,
      updateConversationInFetchState,
      existsInFetchState,
    ]
  );
};
